import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  Users, Clock, Target, Award, Calendar, AlertCircle,
  Activity, DollarSign, Shield, Timer, Building, Trophy,
  Star, ArrowUp, ArrowDown, Info, FileCheck, BadgeCheck,
  Medal, GraduationCap, CheckCircle2, XCircle, Loader2, Bell,
  UserCheck,
  AwardIcon,
  BookOpen
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, ComposedChart
} from 'recharts';
import { analyticsService, TimeRange, TrainingDeadline, DeadlineUser } from '@/services/analytics.service';
import { toast } from '@/components/ui/Toast';
import Modal from '@/components/ui/Modal';
import Pagination from '@/components/ui/Pagination';
import {useLocation,useNavigate } from "react-router-dom";

interface CertificateAnalyticsProps {
  timeRange: string;
  startDate: string;
  endDate: string;
}

// Chart color scheme
const CHART_COLORS = {
  primary: '#6366f1',
  secondary: '#8b5cf6',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
  gray: '#6b7280'
};

// Utility function to convert seconds to readable format
const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.round(seconds % 60);

  if (hours > 0) {
    if (remainingSeconds > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    }
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  if (remainingSeconds > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${minutes}m`;
};

interface MetricCard {
  title: string;
  value: string | number;
  change: string;
  trend: 'up' | 'down' | 'neutral';
  icon: React.ReactNode;
  color: string;
  subtitle: string;
}

interface DepartmentMetric {
  name: string;
  employees: number;
  activeRate: number;
  completionRate: number;
  avgScore: number;
  timeSpent: number;
  timeSpentFormatted: string;
  trend: number;
}

interface LearningTrend {
  period: string;
  enrollments: number;
  completions: number;
  hours: number;
  hoursFormatted: string;
}

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-gray-900 text-white p-3 rounded-lg shadow-lg border border-gray-700">
        <p className="font-medium mb-1">{label}</p>
        {payload.map((entry: any, index: number) => {
          const isTrainingTime = entry.name === 'Training Time';
          const displayValue = isTrainingTime
            ? formatDuration(entry.value)
            : entry.value.toLocaleString();
          return (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {`${entry.name}: ${displayValue}`}
            </p>
          );
        })}
      </div>
    );
  }
  return null;
};

const getPriorityBadge = (priority: string) => {
  switch (priority) {
    case 'high':
      return 'bg-red-100 text-red-700';
    case 'medium':
      return 'bg-yellow-100 text-yellow-700';
    case 'low':
      return 'bg-green-100 text-green-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

function getDaysAhead(timeRange: TimeRange): number {
  const today = new Date();
  switch (timeRange) {
    case 'week':
      return 7;
    case 'month':
      return 30;
    case 'quarter':
      return 90;
    case 'year':
      const year = today.getFullYear();
      const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
      return isLeapYear ? 366 : 365;
    default:
      return 30;
  }
}

export default function CertificateAnalytics({ timeRange, startDate, endDate }: CertificateAnalyticsProps) {
  const [selectedMetric, setSelectedMetric] = useState<'enrollments' | 'completions' | 'hours'>('completions');

  // Deadline pagination and modal state
  const [deadlinePage, setDeadlinePage] = useState(1);
  const [deadlineLimit, setDeadlineLimit] = useState(5);
  const [isDeadlineModalOpen, setIsDeadlineModalOpen] = useState(false);
  const [selectedDeadline, setSelectedDeadline] = useState<TrainingDeadline | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const useCustomDates = timeRange === 'custom' && !!startDate && !!endDate;
  const effectiveTimeRange = timeRange === 'custom' && (!startDate || !endDate) ? 'month' : timeRange;

  // Reset deadline page when time range changes
  useEffect(() => {
    setDeadlinePage(1);
  }, [timeRange]);

  // Fetch analytics overview data
  const { data: overviewData, isLoading: overviewLoading } = useQuery({
    queryKey: ['cert-analytics-overview', effectiveTimeRange, startDate, endDate, useCustomDates],
    queryFn: () => {
      const params: any = {
        compare_previous: true,
        analytics_type: 'certification'
      };

      if (useCustomDates) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else {
        params.time_range = effectiveTimeRange as TimeRange;
      }

      return analyticsService.getOverviewStats(params);
    },
    enabled: timeRange !== 'custom' || useCustomDates,
    placeholderData: (timeRange === 'custom' && !useCustomDates) ? keepPreviousData : undefined,
    refetchInterval: 5 * 60 * 1000,
    onError: () => {
      toast.error('Failed to load certification overview');
    }
  });

  // Transform overview data to metrics cards
  const metrics: MetricCard[] = overviewData ? [
    {
      title: 'Total Employees',
      value: overviewData.total_employees.value.toLocaleString(),
      change: `${overviewData.total_employees.trend === 'up' ? '+' : ''}${overviewData.total_employees.change}`,
      trend: overviewData.total_employees.trend,
      icon: <Users className="w-5 h-5" />,
      color: 'primary',
      subtitle: 'Across all departments',
    },
    {
      title: 'Active Certification Rate',
      value: `${overviewData.active_learning_rate.value.toFixed(1)}%`,
      change: `${overviewData.active_learning_rate.trend === 'up' ? '+' : ''}${overviewData.active_learning_rate.change.toFixed(1)}%`,
      trend: overviewData.active_learning_rate.trend,
      icon: <Activity className="w-5 h-5" />,
      color: 'success',
      subtitle: `${overviewData.active_learning_rate.active_learners_count} actively pursuing`,
    },
    {
      title: 'Avg Completion Rate',
      value: `${overviewData.avg_completion_rate.value.toFixed(1)}%`,
      change: `${overviewData.avg_completion_rate.trend === 'up' ? '+' : overviewData.avg_completion_rate.trend === 'down' ? '-' : ''}${Math.abs(overviewData.avg_completion_rate.change).toFixed(1)}%`,
      trend: overviewData.avg_completion_rate.trend,
      icon: <Target className="w-5 h-5" />,
      color: 'warning',
      subtitle: 'Passed or exhausted attempts',
    },
    {
      title: 'Certification ROI',
      value: `${overviewData.training_roi.value.toFixed(1)}x`,
      change: `${overviewData.training_roi.trend === 'up' ? '+' : ''}${overviewData.training_roi.change.toFixed(1)}x`,
      trend: overviewData.training_roi.trend,
      icon: <DollarSign className="w-5 h-5" />,
      color: 'info',
      subtitle: 'Return on investment',
    },
    {
      title: 'Compliance Certs',
      value: `${overviewData.compliance_rate.value.toFixed(0)}%`,
      change: `${overviewData.compliance_rate.trend === 'up' ? '+' : overviewData.compliance_rate.trend === 'down' ? '-' : ''}${Math.abs(overviewData.compliance_rate.change).toFixed(0)}%`,
      trend: overviewData.compliance_rate.trend,
      icon: <Shield className="w-5 h-5" />,
      color: 'purple',
      subtitle: 'Mandatory certifications',
    },
    {
      title: 'Avg Time to Certify',
      value: `${overviewData.avg_time_to_complete.value.toFixed(1)} ${overviewData.avg_time_to_complete.unit}`,
      change: `${overviewData.avg_time_to_complete.trend === 'down' ? '-' : '+'}${Math.abs(overviewData.avg_time_to_complete.change).toFixed(1)} ${overviewData.avg_time_to_complete.unit}`,
      trend: overviewData.avg_time_to_complete.trend === 'down' ? 'up' : 'down',
      icon: <Timer className="w-5 h-5" />,
      color: 'green',
      subtitle: 'Per certification',
    },
  ] : [];

  // Fetch department performance data
  const { data: departmentData, isLoading: departmentLoading } = useQuery({
    queryKey: ['cert-department-performance', effectiveTimeRange, startDate, endDate, useCustomDates],
    queryFn: () => {
      const params: any = {
        sort_by: 'completion_rate',
        sort_order: 'desc',
        limit: 5,
        analytics_type: 'certification'
      };

      if (useCustomDates) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else {
        params.time_range = effectiveTimeRange as TimeRange;
      }

      return analyticsService.getDepartmentPerformance(params);
    },
    enabled: timeRange !== 'custom' || useCustomDates,
    placeholderData: (timeRange === 'custom' && !useCustomDates) ? keepPreviousData : undefined,
    onError: () => {
      toast.error('Failed to load department performance');
    }
  });

  const departmentMetrics: DepartmentMetric[] = departmentData?.departments.map(dept => ({
    name: dept.department_name,
    sort_name: dept.department_name.length > 15 ? dept.department_name.substring(0, 7) + '...' : dept.department_name,
    employees: dept.employees_count,
    activeRate: dept.active_rate,
    completionRate: dept.completion_rate,
    avgScore: dept.avg_score,
    timeSpent: dept.time_spent_seconds || 0,
    timeSpentFormatted: formatDuration(dept.time_spent_seconds || 0),
    trend: dept.trend_percentage
  })) || [];

  // Fetch learning trends data
  const { data: trendsData, isLoading: trendsLoading } = useQuery({
    queryKey: ['cert-learning-trends', effectiveTimeRange, startDate, endDate, useCustomDates],
    queryFn: () => {
      const params: any = {
        metric_type: 'all',
        analytics_type: 'certification'
      };

      if (useCustomDates) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else {
        params.time_range = effectiveTimeRange as TimeRange;
        params.granularity = effectiveTimeRange === 'week' ? 'daily' : effectiveTimeRange === 'month' ? 'weekly' : 'monthly';
      }

      return analyticsService.getLearningTrends(params);
    },
    enabled: timeRange !== 'custom' || useCustomDates,
    placeholderData: (timeRange === 'custom' && !useCustomDates) ? keepPreviousData : undefined,
    onError: () => {
      toast.error('Failed to load certification trends');
    }
  });

  const certificationTrends: LearningTrend[] = trendsData?.trends.map(trend => ({
    period: trend.period_label,
    enrollments: trend.certification_enrollments || 0,
    completions: trend.certifications_completed || 0,
    hours: trend.training_seconds || 0,
    hoursFormatted: formatDuration(trend.training_seconds || 0)
  })) || [];

  // Fetch top performers data
  const { data: performersData, isLoading: performersLoading } = useQuery({
    queryKey: ['cert-top-performers', effectiveTimeRange, startDate, endDate, useCustomDates],
    queryFn: () => {
      const params: any = {
        metric_type: 'overall',
        limit: 5,
        exclude_admins: false,
        analytics_type: 'certification'
      };

      if (useCustomDates) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else {
        params.time_range = effectiveTimeRange as TimeRange;
      }

      return analyticsService.getTopPerformers(params);
    },
    enabled: timeRange !== 'custom' || useCustomDates,
    placeholderData: (timeRange === 'custom' && !useCustomDates) ? keepPreviousData : undefined,
    onError: () => {
      toast.error('Failed to load top performers');
    }
  });

  const topPerformers = performersData?.performers.map(p => ({
    name: p.user_name,
    department: p.department,
    certCount: p.certifications_completed,
    passRate: p.completion_rate,
    rank: p.rank
  })) || [];

  // Fetch training status
  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['cert-training-status', effectiveTimeRange, startDate, endDate, useCustomDates],
    queryFn: () => {
      const params: any = {
        analytics_type: 'certification'
      };

      if (useCustomDates) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else {
        params.time_range = effectiveTimeRange as TimeRange;
      }

      return analyticsService.getTrainingStatus(params);
    },
    enabled: timeRange !== 'custom' || useCustomDates,
    placeholderData: (timeRange === 'custom' && !useCustomDates) ? keepPreviousData : undefined,
    onError: () => {
      toast.error('Failed to load certification status');
    }
  });

  const certificationStatus = statusData || {
    completed_count: 0,
    in_progress_count: 0,
    not_started_count: 0,
    overdue_count: 0,
  };

  // Fetch training deadlines data
  const { data: deadlinesData, isLoading: deadlinesLoading } = useQuery({
    queryKey: ['cert-training-deadlines', effectiveTimeRange, startDate, endDate, useCustomDates, deadlinePage, deadlineLimit],
    queryFn: () => {
      const params: any = {
        priority_level: 'all',
        include_overdue: false,
        status_filter: 'upcoming',
        limit: deadlineLimit,
        page: deadlinePage,
        sort_by: 'deadline_date',
        analytics_type: 'certification'
      };

      if (useCustomDates) {
        params.from_date = startDate;
        params.to_date = endDate;
      } else {
        params.days_ahead = getDaysAhead(effectiveTimeRange as TimeRange);
      }

      return analyticsService.getTrainingDeadlines(params);
    },
    enabled: timeRange !== 'custom' || useCustomDates,
    staleTime: 0,
    gcTime: 0,
    onError: () => {
      toast.error('Failed to load certification deadlines');
    }
  });

  // Get deadlines from data field (new API) or deadlines field (legacy)
  // Filter out certifications with no users assigned or no user details
  const deadlinesList = (deadlinesData?.data || deadlinesData?.deadlines || []).filter(
    (deadline: TrainingDeadline) => deadline.users && deadline.users.length > 0
  );

  const deadlinesTotal = deadlinesData?.total ?? deadlinesList.length;
  const deadlinesTotalPages = Math.max(1, Math.ceil(deadlinesTotal / deadlineLimit));

  // Handle deadline click to open modal
  const handleDeadlineClick = (deadline: TrainingDeadline) => {
    setSelectedDeadline(deadline);
    setIsDeadlineModalOpen(true);
  };


  const expiringCerts = deadlinesList.map(d => ({
    certification: d.certification_name,
    expiryDate: d.deadline_date,
    affected: d.total_assigned,
    priority: d.priority
  })) || [];

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {overviewLoading ? (
          Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="card animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-6 bg-gray-200 rounded w-1/2 mb-1"></div>
              <div className="h-3 bg-gray-200 rounded w-full"></div>
            </div>
          ))
        ) : (
          metrics.map((metric, index) => (
            <div key={index} className="card card-hover">
              <div className="flex items-start justify-between mb-3">
                <div className={`p-2 rounded-lg ${
                  metric.color === 'primary' ? 'bg-primary-100 text-primary-700' :
                  metric.color === 'success' ? 'bg-green-100 text-green-700' :
                  metric.color === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                  metric.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                  metric.color === 'green' ? 'bg-emerald-100 text-emerald-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {metric.icon}
                </div>
                <span className={`flex items-center gap-1 text-xs font-medium ${
                  metric.trend === 'up' ? 'text-green-600' :
                  metric.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {metric.trend === 'up' ? <ArrowUp className="w-3 h-3" /> :
                   metric.trend === 'down' ? <ArrowDown className="w-3 h-3" /> : null}
                  {metric.change}
                </span>
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{metric.value}</p>
                <p className="text-xs text-gray-500 mt-1">{metric.title}</p>
                <p className="text-xs text-gray-400 mt-1">{metric.subtitle}</p>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Certification Trends Chart */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Certification Activity Trends</h2>
              <select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value as any)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="enrollments">Enrollments</option>
                <option value="completions">Completions</option>
                <option value="hours">Training Time</option>
              </select>
            </div>

            {trendsLoading ? (
              <div className="h-64 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : certificationTrends.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No trend data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={256}>
                <ComposedChart data={certificationTrends}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis
                    dataKey="period"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{
                      paddingTop: '20px',
                      fontSize: '14px'
                    }}
                  />
                  {selectedMetric === 'enrollments' && (
                    <Bar
                      dataKey="enrollments"
                      name="Enrollments"
                      fill={CHART_COLORS.info}
                      fillOpacity={0.8}
                    />
                  )}
                  {selectedMetric === 'completions' && (
                    <>
                      <Bar
                        dataKey="completions"
                        name="Completions"
                        fill={CHART_COLORS.success}
                        fillOpacity={0.8}
                      />
                      <Line
                        type="monotone"
                        dataKey="enrollments"
                        name="Enrollments"
                        stroke={CHART_COLORS.primary}
                        strokeWidth={2}
                        dot={{ fill: CHART_COLORS.primary, r: 3 }}
                      />
                    </>
                  )}
                  {selectedMetric === 'hours' && (
                    <>
                      <Bar
                        dataKey="hours"
                        name="Training Time"
                        fill={CHART_COLORS.secondary}
                        fillOpacity={0.8}
                      />
                      <Line
                        type="monotone"
                        dataKey="completions"
                        name="Completions"
                        stroke={CHART_COLORS.success}
                        strokeWidth={2}
                        dot={{ fill: CHART_COLORS.success, r: 3 }}
                      />
                    </>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            )}

            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-gray-600">
                Total {selectedMetric === 'hours' ? 'Training Time' : selectedMetric}: <span className="font-semibold text-gray-900">
                  {selectedMetric === 'hours'
                    ? formatDuration(certificationTrends.reduce((sum, p) => sum + p.hours, 0))
                    : certificationTrends.reduce((sum, p) =>
                        sum + (selectedMetric === 'enrollments' ? p.enrollments : p.completions), 0
                      ).toLocaleString()
                  }
                </span>
              </span>
            </div>
          </div>

          {/* Department Certification Performance */}
          <div className="card mt-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Department Certification Performance</h2>
              <Link to="/admin/analytics/department-progress" className="text-sm text-primary-600 hover:text-primary-700">
                View Details →
              </Link>
            </div>

            {departmentLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : departmentMetrics.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Building className="w-16 h-16 text-gray-300 mb-4" />
                <p className="text-gray-500 font-medium text-lg">No department performance data found</p>
                <p className="text-sm text-gray-400 mt-2">No department data available for the selected period</p>
              </div>
            ) : (
              <>
                {/* Department Performance Bar Chart */}
                <div className="mb-6">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={departmentMetrics.slice(0, 8)}
                      layout="horizontal"
                      margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                    >
                      <defs>
                        <linearGradient id="certCompletionGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity={0.8}/>
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0.5}/>
                        </linearGradient>
                        <linearGradient id="certActiveGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#6366f1" stopOpacity={0.8}/>
                          <stop offset="100%" stopColor="#6366f1" stopOpacity={0.5}/>
                        </linearGradient>
                        <linearGradient id="certScoreGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.8}/>
                          <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.5}/>
                        </linearGradient>
                      </defs>

                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />

                      <XAxis
                        type="category"
                        dataKey="sort_name"
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        tick={{
                          fontSize: 13,
                          fill: '#374151',
                          fontWeight: 500,
                          dy: 10,
                        }}
                        height={70}
                        padding={{ left: 20, right: 20 }}
                      />

                      <YAxis
                        type="number"
                        domain={[0, 100]}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#6b7280' }}
                        label={{
                          value: 'Percentage (%)',
                          angle: -90,
                          position: 'insideLeft',
                          style: { fill: '#6b7280', fontSize: 12 }
                        }}
                      />

                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-white p-4 rounded-xl shadow-2xl border border-indigo-100">
                                <p className="font-bold text-gray-800 mb-3 text-base border-b border-gray-200 pb-2">
                                  {data.name}
                                </p>
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center gap-8">
                                    <span className="text-gray-600 text-sm">Completion Rate:</span>
                                    <span className="font-semibold text-green-600">{data.completionRate}%</span>
                                  </div>
                                  <div className="flex justify-between items-center gap-8">
                                    <span className="text-gray-600 text-sm">Active Rate:</span>
                                    <span className="font-semibold text-indigo-600">{data.activeRate}%</span>
                                  </div>
                                  <div className="flex justify-between items-center gap-8">
                                    <span className="text-gray-600 text-sm">Avg Score:</span>
                                    <span className="font-semibold text-amber-600">{data.avgScore}%</span>
                                  </div>
                                  <div className="flex justify-between items-center gap-8">
                                    <span className="text-gray-600 text-sm">Employees:</span>
                                    <span className="font-semibold text-gray-800">{data.employees}</span>
                                  </div>
                                  <div className="flex justify-between items-center gap-8">
                                    <span className="text-gray-600 text-sm">Training Time:</span>
                                    <span className="font-semibold text-purple-600">{data.timeSpentFormatted}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                        cursor={{ fill: 'rgba(99, 102, 241, 0.05)' }}
                      />

                      <Legend
                        verticalAlign="top"
                        height={50}
                        iconType="circle"
                        wrapperStyle={{
                          paddingBottom: '20px',
                          fontSize: '13px',
                          fontWeight: 500
                        }}
                      />

                      <Bar
                        dataKey="completionRate"
                        name="Completion Rate"
                        fill="url(#certCompletionGradient)"
                        radius={[8, 8, 0, 0]}
                        barSize={18}
                      />
                      <Bar
                        dataKey="activeRate"
                        name="Active Rate"
                        fill="url(#certActiveGradient)"
                        radius={[8, 8, 0, 0]}
                        barSize={18}
                      />
                      <Bar
                        dataKey="avgScore"
                        name="Avg Score"
                        fill="url(#certScoreGradient)"
                        radius={[8, 8, 0, 0]}
                        barSize={18}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Department</th>
                        <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Employees</th>
                        <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Active Rate</th>
                        <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Completion</th>
                        <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Avg Score</th>
                        <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Trend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departmentMetrics.map((dept, index) => (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Building className="w-4 h-4 text-gray-400" />
                              <span className="font-medium text-gray-900">{dept.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center text-sm text-gray-600">
                            {dept.employees}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="text-sm font-medium text-gray-900">{dept.activeRate}%</span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${
                                    dept.completionRate >= 85 ? 'bg-green-500' :
                                    dept.completionRate >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${dept.completionRate}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium">{dept.completionRate}%</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              dept.avgScore >= 90 ? 'bg-green-100 text-green-700' :
                              dept.avgScore >= 80 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {dept.avgScore}%
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`flex items-center justify-center gap-1 text-xs font-medium ${
                              dept.trend > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {dept.trend > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                              {Math.abs(dept.trend)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Certification Status Distribution - Donut Chart */}
          <div className="card mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Certification Status Overview</h3>
              <Info className="w-4 h-4 text-gray-400" />
            </div>
            {statusLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : certificationStatus && Object.values(certificationStatus).some(val => val > 0) ? (
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie
                    data={Object.entries(certificationStatus).map(([key, value]) => ({
                      name: key.replace('_count', '').replace('_', ' '),
                      value,
                      color:
                        key === 'completed_count'
                          ? CHART_COLORS.success
                          : key === 'in_progress_count'
                          ? CHART_COLORS.warning
                          : key === 'not_started_count'
                          ? CHART_COLORS.gray
                          : CHART_COLORS.error
                    }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={60}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {Object.entries(certificationStatus).map(([key], index) => {
                      const color =
                        key === 'completed_count'
                          ? CHART_COLORS.success
                          : key === 'in_progress_count'
                          ? CHART_COLORS.warning
                          : key === 'not_started_count'
                          ? CHART_COLORS.gray
                          : CHART_COLORS.error;
                      return <Cell key={`cell-${index}`} fill={color} />;
                    })}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="middle"
                    align="right"
                    layout="vertical"
                    wrapperStyle={{ paddingLeft: '20px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Activity className="w-12 h-12 text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">No certification status found</p>
                <p className="text-xs text-gray-400 mt-1">No status data available for the selected period</p>
              </div>
            )}
          </div>

          {/* Top Certified Employees */}
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-5 h-5 text-yellow-500" />
              <h3 className="text-lg font-semibold text-gray-900">Top Certified Employees</h3>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
              <Medal className="w-4 h-4 text-yellow-400" />
              <p>
                <span className='text-gray-900'>Ranked Based on</span> <span className="font-semibold text-yellow-500">Total certifications completed</span>
              </p>
            </div>
            {performersLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : topPerformers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Trophy className="w-12 h-12 text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">No top performers found</p>
                <p className="text-xs text-gray-400 mt-1">No certification data available for the selected period</p>
              </div>
            ) : (
              <div className="space-y-3">
                {topPerformers.map((performer) => (
                  <div key={performer.rank} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      performer.rank === 1 ? 'bg-yellow-100 text-yellow-700' :
                      performer.rank === 2 ? 'bg-orange-100 text-orange-700' :
                      performer.rank === 3 ? 'bg-gray-100 text-gray-700' :
                      'bg-gray-50 text-gray-600'
                    }`}>
                      {performer.rank}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{performer.name}</p>
                      <p className="text-xs text-gray-500">{performer.department}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">{performer.passRate}%</p>
                      <p className="text-xs text-gray-500">{performer.certCount} certs</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Expiring Certifications */}
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <h3 className="text-lg font-semibold text-gray-900">Upcoming Deadlines</h3>
            </div>
            {deadlinesLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg animate-pulse">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : deadlinesList.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Calendar className="w-12 h-12 text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">No upcoming deadlines found</p>
                <p className="text-xs text-gray-400 mt-1">No certification deadlines for the selected period</p>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {deadlinesList.map((deadline, index) => (
                    <div
                      key={`${deadline.assignment_id}-${deadline.certification_id}-${index}`}
                      className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleDeadlineClick(deadline)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <p className="text-sm font-medium text-gray-900 line-clamp-1">{deadline.certification_name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getPriorityBadge(deadline.priority)}`}>
                          {deadline.priority}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <Calendar className="w-3 h-3" />
                          {new Date(deadline.deadline_date).toLocaleDateString()}
                        </div>
                        <span className="text-xs text-gray-600">
                          {deadline.total_assigned} Learners
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination - always shown so the current page and total record count stay visible, even when all records fit on one page */}
                {deadlinesTotal > 0 && (
                  <div className="mt-4 -mx-4 -mb-4">
                    <Pagination
                      currentPage={deadlinePage}
                      totalPages={deadlinesTotalPages}
                      totalItems={deadlinesTotal}
                      itemsPerPage={deadlineLimit}
                      onPageChange={setDeadlinePage}
                      onItemsPerPageChange={setDeadlineLimit}
                      showItemsPerPage={false}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Deadline Users Modal */}
<Modal
            isOpen={isDeadlineModalOpen}
            onClose={() => {
              setIsDeadlineModalOpen(false);
              setSelectedDeadline(null);
            }}
            title="Upcoming Deadline Details"
            subtitle="Manage assigned users and send reminders"
            size="lg"
          >
            {selectedDeadline && (
              <div className="space-y-6">
                {/* Course Info Summary */}
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-100 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-white rounded-lg text-indigo-600 shadow-sm ring-1 ring-indigo-100">
                      <BookOpen className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-base font-bold text-gray-900">{selectedDeadline.certification_name}</h4>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                            <span className="flex items-center gap-1.5 bg-white px-2.5 py-1 rounded-md border border-gray-200/50 shadow-sm">
                              <Calendar className="w-4 h-4 text-indigo-500" />
                              Due: <span className="font-medium text-gray-900">{new Date(selectedDeadline.deadline_date).toLocaleDateString()}</span>
                            </span>
                            <span className="flex items-center gap-1.5 bg-white px-2.5 py-1 rounded-md border border-gray-200/50 shadow-sm">
                              <Users className="w-4 h-4 text-indigo-500" />
                              <span className="font-medium text-gray-900">{selectedDeadline.users?.length || 0}</span> Assigned
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/admin/certification-performance/${selectedDeadline?.certification_id}/performance`)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-white border border-indigo-200 rounded-lg hover:bg-indigo-50 hover:border-indigo-300 transition-all shadow-sm"
                        >
                          <Trophy className="w-3.5 h-3.5" />
                          View 
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Users List */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      Assigned Employees
                    </h4>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      {selectedDeadline.users?.length || 0} Total
                    </span>
                  </div>

                  <div className="space-y-3">
                    {selectedDeadline.users?.map((user) => (
                      <div
                        key={user.user_id}
                        className="group flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-white hover:border-indigo-200 hover:shadow-md hover:shadow-indigo-500/5 transition-all duration-200"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-sm ring-2 ring-white ${
                          'bg-gradient-to-br from-gray-400 to-gray-600'
                          }`}>
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-indigo-700 transition-colors">
                              {user.name}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          {/* Actions */}
                          <div className="flex items-center gap-1">
                            {/* Progress Button */}
                            <button
                              onClick={() =>
                                navigate(
                                  `/admin/users/${user?.user_id}/certifications/${selectedDeadline?.certification_id}/progress`,
                                  {
                                    state: {
                                      from: location.pathname + location.search,
                                      originalFrom:
                                        (location.state as any)?.originalFrom ??
                                        location.pathname + location.search,
                                    },
                                  }
                                )
                            }
                              className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                              title="View Progress"
                            >
                              <UserCheck className="w-4 h-4" />
                            </button>

                            {/* Reminder Button */}
                            <button
                            title='Send Reminder'
                            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"

                              onClick={(e) => {
                                e.stopPropagation();
                  if (!user.expired && !user.notify) {
                    toast.warning("User has completed the course");
                  } else if (!user.expired && user.notify) {
                    toast.success("Initiated deadline reminder");
                    analyticsService
                      .sendDeadlineReminder({
                        user_id: user.user_id,
                        type: "certification",
                        certification_id: selectedDeadline?.certification_id,
                      })
                      .catch(() => {});
                  } else if (user.expired && !user.notify) {
                    toast.warning("Deadline has passed");
                  }
                }}
                            >
                              <Bell className={`w-4 h-4 ${!user.status && 'animate-pulse'}`} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Modal>
    </div>
  );
}
